import { GameConfig } from "../config/GameConfig";
import { GameNavigator } from "../core/Navigation";
import { AdFarmSession } from "../integration/AdFarmSession";
import { MobileOverview, MobileTask } from "../integration/AdFarmTypes";
import { RewardAdBridge } from "../integration/RewardAdBridge";
import { AppTabBar } from "../ui/AppTabBar";
import { Theme } from "../ui/Theme";
import { UIFactory } from "../ui/UIFactory";
import { BaseScreen } from "./BaseScreen";

abstract class AccountBaseScreen extends BaseScreen {
    protected readonly session = AdFarmSession.instance;

    protected buildBackground(titleText: string, subtitle: string): Laya.Box {
        this.graphics.drawRect(0, 0, this.width, this.height, "#E94B31");
        this.graphics.drawRect(0, 0, this.width, 245, "#0B4775");
        this.graphics.drawCircle(60, 205, 130, "#14638D");
        this.graphics.drawCircle(700, 175, 150, "#125B86");
        const title = UIFactory.label(titleText, 49, "#FFE4A2", 620, 72, true);
        title.pos(65, 58);
        title.stroke = 4;
        title.strokeColor = "#84251C";
        this.addChild(title);
        const sub = UIFactory.label(subtitle, 23, "#FFF5D6", 620, 45, true);
        sub.pos(65, 132);
        this.addChild(sub);
        const panel = UIFactory.panel(650, 1180);
        panel.pos(50, 220);
        this.addChild(panel);
        return panel;
    }

    protected errorMessage(error: unknown): string {
        return error instanceof Error ? error.message : "操作失败，请稍后重试";
    }
}

export class TaskScreen extends AccountBaseScreen {
    private readonly rewardAds = new RewardAdBridge();
    private readonly listLayer = new Laya.Box();
    private readonly summary = UIFactory.label("正在同步后台任务…", 25, Theme.ink, 560, 70, true);
    private busy = false;

    constructor(nav: GameNavigator) {
        super(nav);
        const panel = this.buildBackground("红包任务", "完整观看激励视频后可领取，实际收益以到账为准");
        this.summary.pos(45, 28);
        this.summary.width = 380;
        this.summary.align = "left";
        panel.addChild(this.summary);
        this.listLayer.pos(35, 100);
        this.listLayer.width = 580;
        this.listLayer.height = 820;
        panel.addChild(this.listLayer);
        const refresh = UIFactory.button("刷新", 145, 58, () => this.refreshFromServer(), "green", 23);
        refresh.pos(455, 28);
        panel.addChild(refresh);
        this.addChild(new AppTabBar(nav, "tasks"));
        this.session.on(AdFarmSession.OVERVIEW_CHANGED, this, this.render);
        this.render(this.session.overview);
    }

    destroy(destroyChild = true): void {
        this.session.off(AdFarmSession.OVERVIEW_CHANGED, this, this.render);
        super.destroy(destroyChild);
    }

    private render(overview: MobileOverview | null): void {
        this.listLayer.removeChildren();
        const account = overview ? this.session.revenue.accountFor(overview, this.session.role) : null;
        const tasks = this.session.role === "secondaryAgent" ? overview?.secondaryAgentTasks : overview?.tasks;
        this.summary.text = `今日剩余 ${Number(account?.availableViews || 0)} 次 · 后台余额 ¥${Number(account?.totalBalance || 0).toFixed(2)}`;
        if (!tasks?.length) {
            const empty = UIFactory.label("后台暂未配置可领取的广告任务\n请稍后刷新", 27, "#8B5B43", 560, 160, true);
            empty.pos(10, 170);
            empty.wordWrap = true;
            this.listLayer.addChild(empty);
            return;
        }
        tasks.slice(0, 8).forEach((task, index) => this.addTask(task, index));
    }

    private addTask(task: MobileTask, index: number): void {
        const card = new Laya.Box();
        card.pos(0, index * 105);
        card.width = 580;
        card.height = 94;
        UIFactory.roundRect(card, 0, 4, 580, 90, 15, "#9B3A2555");
        UIFactory.roundRect(card, 0, 0, 580, 90, 15, "#FFF0C9", "#D48B45", 3);
        const title = UIFactory.label(task.title || "观看激励视频", 22, Theme.ink, 365, 34, false);
        title.align = "left";
        title.pos(22, 7);
        card.addChild(title);
        const reward = Number(task.reward ?? task.amount ?? 0);
        const desc = UIFactory.label(`${this.sourceName(task.sourceKey)} · 奖励 ¥${reward.toFixed(2)}`, 17, "#9A4A2C", 365, 28, false);
        desc.align = "left";
        desc.pos(22, 47);
        card.addChild(desc);
        const action = UIFactory.button("去完成", 145, 54, () => this.startTask(task), "red", 23);
        action.pos(410, 16);
        card.addChild(action);
        this.listLayer.addChild(card);
    }

    private async startTask(task: MobileTask): Promise<void> {
        if (this.busy) return;
        this.busy = true;
        try {
            const status = await this.rewardAds.show(task, message => this.nav.toast(message));
            this.nav.toast(status.verified || status.sessionStatus === "settled" ? "任务完成，收益已更新" : "奖励确认中，请稍后刷新");
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        } finally {
            this.busy = false;
        }
    }

    private async refreshFromServer(): Promise<void> {
        try {
            await this.session.refreshOverview();
            this.nav.toast("任务已刷新");
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        }
    }

    private sourceName(source?: string): string {
        const names: Record<string, string> = { pangle: "穿山甲", kuaishou: "快手", baidu: "百度", youlianghui: "优量汇" };
        return names[source || ""] || "激励广告";
    }
}

export class MineScreen extends AccountBaseScreen {
    private readonly content = new Laya.Box();

    constructor(nav: GameNavigator) {
        super(nav);
        const panel = this.buildBackground("我的", "账号、邀请、教程与收款设置");
        this.content.pos(35, 35);
        this.content.width = 580;
        this.content.height = 1030;
        panel.addChild(this.content);
        this.addChild(new AppTabBar(nav, "mine"));
        this.session.on(AdFarmSession.OVERVIEW_CHANGED, this, this.render);
        this.render(this.session.overview);
    }

    destroy(destroyChild = true): void {
        this.session.off(AdFarmSession.OVERVIEW_CHANGED, this, this.render);
        super.destroy(destroyChild);
    }

    private render(overview: MobileOverview | null): void {
        this.content.removeChildren();
        const account = overview ? this.session.revenue.accountFor(overview, this.session.role) : null;
        const profile = new Laya.Box();
        profile.width = 580;
        profile.height = 210;
        UIFactory.roundRect(profile, 0, 0, 580, 210, 20, "#E94B31", "#F2B75A", 4);
        profile.graphics.drawCircle(82, 92, 52, "#FFE0A2", "#A74429", 4);
        const avatar = UIFactory.label("成", 39, Theme.redDark, 92, 92, true);
        avatar.pos(36, 46);
        profile.addChild(avatar);
        const name = UIFactory.label(account?.name || "成语玩家", 31, "#FFF5D2", 350, 46, false);
        name.align = "left";
        name.pos(160, 35);
        profile.addChild(name);
        const phone = UIFactory.label(this.mask(account?.phone || ""), 22, "#FFE0B0", 350, 38, false);
        phone.align = "left";
        phone.pos(160, 86);
        profile.addChild(phone);
        const role = UIFactory.label(this.session.role === "user" ? "普通用户" : "二级代理", 22, "#6B2818", 150, 42, true);
        role.pos(160, 132);
        role.bgColor = "#FFE5AC";
        profile.addChild(role);
        this.content.addChild(profile);

        this.infoRow("后台余额", `¥${Number(account?.totalBalance || 0).toFixed(2)}`, 240, () => this.nav.navigate("wallet"));
        this.infoRow("我的邀请码", account?.inviteCode || "--", 350, () => this.copyInvite(account?.inviteCode || ""));
        this.infoRow("收款账户", account?.payoutIdentity ? this.mask(account.payoutIdentity) : "去绑定", 460, () => this.nav.navigate("wallet"));
        this.infoRow("教程与客服", overview?.admin?.tutorial?.title || "查看教程", 570, () => this.showTutorial(overview));

        const refresh = UIFactory.button("刷新账号", 245, 76, () => this.refreshAccount(), "green", 28);
        refresh.pos(25, 730);
        this.content.addChild(refresh);
        const logout = UIFactory.button("退出登录", 245, 76, () => this.session.logout(), "dark", 28);
        logout.pos(310, 730);
        this.content.addChild(logout);
        const version = UIFactory.label("成语红包王 v1.0.0", 20, "#9D735C", 560, 45, true);
        version.pos(10, 850);
        this.content.addChild(version);
    }

    private infoRow(title: string, value: string, y: number, onClick: () => void): void {
        const row = new Laya.Box();
        row.pos(0, y);
        row.width = 580;
        row.height = 92;
        row.mouseEnabled = true;
        UIFactory.roundRect(row, 0, 0, 580, 92, 15, "#FFF0C9", "#D69A5B", 2);
        const left = UIFactory.label(title, 25, Theme.ink, 240, 80, false);
        left.align = "left";
        left.pos(24, 6);
        row.addChild(left);
        const right = UIFactory.label(`${value}  ›`, 23, "#A14D2D", 290, 80, false);
        right.align = "right";
        right.pos(260, 6);
        row.addChild(right);
        row.on(Laya.Event.CLICK, row, onClick);
        this.content.addChild(row);
    }

    private async refreshAccount(): Promise<void> {
        try {
            await this.session.refreshOverview();
            this.nav.toast("账号信息已更新");
        } catch (error) {
            this.nav.toast(this.errorMessage(error));
        }
    }

    private async copyInvite(code: string): Promise<void> {
        if (!code) {
            this.nav.toast("后台尚未生成邀请码");
            return;
        }
        const link = `${this.session.api.baseUrl}/invite?code=${encodeURIComponent(code)}`;
        try {
            await globalThis.navigator?.clipboard?.writeText(link);
            this.nav.toast("邀请链接已复制");
        } catch (_error) {
            this.nav.toast(`邀请码：${code}`);
        }
    }

    private showTutorial(overview: MobileOverview | null): void {
        const tutorial = overview?.admin?.tutorial;
        const support = [tutorial?.supportPhone, tutorial?.supportWechat].filter(Boolean).join(" / ");
        this.nav.toast(`${tutorial?.content || "后台暂未配置教程内容"}${support ? `\n客服：${support}` : ""}`);
    }

    private mask(value: string): string {
        if (!value) return "--";
        if (value.length <= 6) return value;
        return `${value.slice(0, 3)}****${value.slice(-3)}`;
    }
}
